import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, fmtUSD } from "../lib/api";
import {
  Package, TrendUp, TrendDown, Equals, ShieldCheckered, Calculator, Lightning,
} from "@phosphor-icons/react";

/**
 * Category Intelligence — high-fidelity Data Package modal.
 *
 *   Section 1 · Price Movement Drivers  (FRED/BLS factors + micro)
 *   Section 2 · Calculation Trace       (how the should-cost was built)
 *   Section 3 · Decision Integrity (PDI) (signals + weights → 0-100)
 */
export default function CategoryPackageModal({ open, onOpenChange, segmentCode, segmentName }) {
  const [pkg, setPkg] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !segmentCode) return;
    setPkg(null);
    setErr(null);
    api.categoryPackage(segmentCode).then(setPkg).catch((e) => setErr(e?.message || "Package unavailable"));
  }, [open, segmentCode]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="category-package-modal"
        className="max-w-4xl bg-slate-900 border border-slate-700 text-slate-50 p-0 max-h-[92vh] overflow-hidden"
      >
        <DialogHeader className="px-6 py-4 border-b border-slate-800 bg-slate-950">
          <DialogTitle className="font-display text-lg flex items-center gap-2">
            <Package weight="duotone" className="text-emerald-400" size={20} />
            Category Intelligence · {segmentName || pkg?.segment_name || segmentCode}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500 font-mono mt-1">
            {segmentCode} · {pkg?.category || "—"} · {pkg?.po_count ?? "—"} POs in scope
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-auto thin-scroll p-6 space-y-5 max-h-[78vh]">
          {err && (
            <div className="border border-rose-500/40 bg-rose-500/10 text-rose-200 p-3 rounded-sm">
              {err}
            </div>
          )}
          {!pkg && !err && <Skeleton />}

          {pkg && (
            <>
              {/* ① Price Movement Drivers */}
              <Section
                icon={Lightning}
                title="Price Movement Drivers"
                subtitle="Macro & micro factors pressing on this category"
              >
                <ul className="space-y-2" data-testid="drivers-list">
                  {pkg.price_movement.map((d, i) => (
                    <li
                      key={i}
                      data-testid={`driver-${d.code}`}
                      className="flex items-start gap-3 border border-slate-800 bg-slate-950 rounded-sm p-3"
                    >
                      <MomChip mom={d.mom_pct} trend={d.trend} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-3">
                          <div className="font-display text-[13px] font-semibold text-slate-50">
                            {d.label}
                          </div>
                          <div className="font-mono text-[10px] text-slate-500 tracking-mono-label">
                            {d.source} · {d.code}
                          </div>
                        </div>
                        {d.narrative && (
                          <p className="text-[11px] text-slate-400 mt-1 leading-snug">
                            {d.narrative}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </Section>

              {/* ② Calculation Trace */}
              <Section
                icon={Calculator}
                title="Calculation Trace"
                subtitle={`Should-Cost build · baseline ${fmtUSD(pkg.totals.baseline)} · realized ${fmtUSD(pkg.totals.realized)}`}
              >
                <ol
                  className="space-y-1.5 border border-slate-800 bg-slate-950 rounded-sm p-3"
                  data-testid="calc-trace"
                >
                  {pkg.calculation_trace.map((s, i) => (
                    <li
                      key={i}
                      className="grid grid-cols-12 gap-2 text-[12px] py-1.5 border-b last:border-b-0 border-slate-800"
                    >
                      <span className="col-span-1 font-mono text-[10px] text-slate-500 tracking-mono-label">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="col-span-8 text-slate-200 leading-snug">{s.step}</span>
                      <span className="col-span-3 text-right font-mono text-emerald-300">
                        {typeof s.value === "number" ? fmtUSD(s.value) : s.value}
                      </span>
                    </li>
                  ))}
                </ol>
              </Section>

              {/* ③ Decision Integrity (PDI) */}
              <Section
                icon={ShieldCheckered}
                title="Decision Integrity (PDI)"
                subtitle={`PDI ${pkg.pdi.value} · ${pkg.pdi.tier} band · signal-weighted`}
              >
                <div
                  data-testid="pdi-breakdown"
                  className="grid grid-cols-1 md:grid-cols-5 gap-3 items-stretch"
                >
                  <div
                    className={`md:col-span-1 flex flex-col items-center justify-center rounded-sm p-4 tier-${pkg.pdi.tier}`}
                  >
                    <div className="font-display text-4xl font-bold leading-none">
                      {Math.round(pkg.pdi.value)}
                    </div>
                    <div className="font-mono text-[10px] tracking-mono-label mt-1 opacity-80">
                      PDI · {pkg.pdi.tier}
                    </div>
                  </div>
                  <ul className="md:col-span-4 space-y-1.5">
                    {pkg.pdi.signals.map((s, i) => (
                      <li
                        key={i}
                        data-testid={`pdi-signal-${i}`}
                        className="grid grid-cols-12 gap-2 items-center border border-slate-800 bg-slate-950 rounded-sm p-2.5"
                      >
                        <span className="col-span-6 text-[12px] text-slate-200 leading-snug">{s.signal}</span>
                        <div className="col-span-4 h-2 bg-slate-800 rounded-sm overflow-hidden">
                          <div
                            className="h-full bg-emerald-500/70"
                            style={{ width: `${Math.round(s.weight * 100)}%` }}
                          />
                        </div>
                        <span className="col-span-1 text-[10px] font-mono text-slate-500 text-right">
                          {Math.round(s.weight * 100)}%
                        </span>
                        <span className="col-span-1 text-[11px] font-mono text-emerald-300 text-right truncate">
                          {s.raw}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Section>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <section data-testid={`section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <header className="flex items-center gap-2 mb-2">
        <Icon weight="duotone" className="text-emerald-400" size={16} />
        <div>
          <h3 className="font-display text-sm font-bold text-slate-50 leading-none">{title}</h3>
          <p className="text-[11px] text-slate-500 mt-0.5 font-mono">{subtitle}</p>
        </div>
      </header>
      {children}
    </section>
  );
}

function MomChip({ mom = 0, trend = "flat" }) {
  const Icon = trend === "rising" ? TrendUp : trend === "falling" ? TrendDown : Equals;
  const tone = trend === "rising" ? "text-rose-300" : trend === "falling" ? "text-emerald-300" : "text-slate-400";
  return (
    <span
      className={`inline-flex items-center gap-1 font-mono text-[11px] ${tone} shrink-0 min-w-[68px]`}
      title={trend}
    >
      <Icon weight="bold" size={14} />
      {(mom >= 0 ? "+" : "") + Number(mom).toFixed(1)}%
    </span>
  );
}

function Skeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="h-20 skeleton rounded-sm" />
      ))}
    </div>
  );
}
